'use client'

import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import MUITypography from '@mui/material/Typography'
import MUIStack from '@mui/material/Stack'
import MUIIconButton from '@mui/material/IconButton'
import { PlayPauseIcon } from '@repo/ui/PlayPauseIcon'
import { TimeLedgerPostingIcon } from '@repo/ui/TimeLedgerPostingIcon'
import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { useTimeRecordingStore } from '@repo/store/useTimeRecordingStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { TimeRecordingTimeDisplay } from '../TimeRecordingTimeDisplay/TimeRecordingTimeDisplay'

export const TimeRecordingDrawerEntry = ({ entry, onClick }) => {
  const {
    matterTimerId,
    clientNo,
    matterNo,
    description,
    isPlaying,
    matterDescription,
    activityDescription,
    timeRecordingActivityDescription
  } = entry || {}

  const isTimer = matterTimerId ? true : false
  const { resumeTimer, pauseTimer } = useTimeRecordingStore()
  const [timerIsPlaying, setTimerIsPlaying] = useState(isPlaying)
  const { isOutlookAddin } = useGlobalSettingsStore()

  useEffect(() => {
    if (!isTimer) return
    setTimerIsPlaying(entry?.isPlaying || false)
  }, [isTimer, entry, entry?.isPlaying])

  const handleIconClick = (e) => {
    e.stopPropagation()

    if (timerIsPlaying) {
      setTimerIsPlaying(false)
      pauseTimer(entry)
    } else {
      setTimerIsPlaying(true)
      resumeTimer(entry)
    }
  }

  const handleClick = () => {
    if (onClick && entry.matterTimerId) onClick(entry)
  }

  return (
    <MUIStack
      data-testid="TimeRecordingDrawerEntry"
      direction="row"
      alignItems="center"
      sx={{
        padding: '9px 0px 9px 12px',
        backgroundColor: timerIsPlaying ? 'secondary.focus' : 'rgba(61, 81, 123, 0.04)',
        borderRadius: '4px',
        transition: 'all 250ms',
        cursor: isTimer ? 'pointer' : 'default',
        '&:hover': { backgroundColor: matterTimerId ? 'secondary.focus' : 'rgba(61, 81, 123, 0.04)' },
        '&:hover [data-testid="ChevronRightIcon"]': { opacity: 1 }
      }}
      gap={2}
      onClick={handleClick}
    >
      {renderIcon(isTimer, timerIsPlaying, handleIconClick)}

      <MUIStack
        direction="column"
        justifyContent="center"
        sx={{
          flex: 1,
          paddingRight: isOutlookAddin ? 0 : '20px',
          userSelect: 'none',
          maxWidth: isOutlookAddin ? '160px' : '330px'
        }}
      >
        {renderMatterDetails(clientNo, matterNo, matterDescription, isOutlookAddin)}

        {renderDescription(isTimer, description, activityDescription, timeRecordingActivityDescription)}
      </MUIStack>

      <TimeRecordingTimeDisplay
        isTimer={isTimer}
        timerIsPlaying={timerIsPlaying}
        syncTime={isOutlookAddin}
        entry={entry}
        sx={{
          color: timerIsPlaying ? 'secondary.main' : 'text.secondary',
          maxWidth: isOutlookAddin ? '40px' : 'none',
          minWidth: isOutlookAddin ? '40px' : '60px'
        }}
      />
    </MUIStack>
  )
}

const renderIcon = (isTimer, timerIsPlaying, handleIconClick) => {
  if (isTimer)
    return (
      <MUIIconButton sx={{ width: '50px', minWidth: '50px', height: '50px', padding: '3px' }} onClick={handleIconClick}>
        <PlayPauseIcon isPlaying={timerIsPlaying} sx={{ width: 44, height: 44 }} />
      </MUIIconButton>
    )
  return (
    <MUIStack sx={{ width: '50px', minWidth: '50px', height: '50px' }} alignItems="center" justifyContent="center">
      <TimeLedgerPostingIcon />
    </MUIStack>
  )
}

const renderMatterDetails = (clientNo, matterNo, matterDescription, isOutlookAddin) => {
  if (clientNo) {
    return (
      <MUIStack
        direction={'row'}
        alignItems={'center'}
        sx={{ width: '100%', maxWidth: isOutlookAddin ? '160px' : '330px' }}
        gap={2}
      >
        <ClientMatterDetailsPill clientNo={clientNo} matterNo={matterNo} sx={{ top: '2px' }} />
        <MUITypography
          variant="body1"
          sx={{
            mb: 0,
            flex: 1,
            userSelect: 'none',
            pointerEvents: 'none',
            maxWidth: isOutlookAddin ? '160px' : '330px',
            fontSize: isOutlookAddin ? '12px' : '14px'
          }}
          noWrap
        >
          {matterDescription || ''}
        </MUITypography>
      </MUIStack>
    )
  }

  return (
    <MUITypography
      variant="body1"
      sx={{ mb: 0, color: 'text.primary', userSelect: 'none', pointerEvents: 'none' }}
      noWrap
    >
      Blank matter
    </MUITypography>
  )
}

const renderDescription = (
  isTimer,
  description,
  activityDescription,
  timeRecordingActivityDescription,
  isOutlookAddin
) => {
  return (
    <MUIStack
      direction="row"
      alignItems="center"
      sx={{ width: '100%', maxWidth: isOutlookAddin ? '160px' : '330px' }}
      gap={2}
    >
      <MUITypography
        variant="body1"
        sx={{
          mb: 0,
          color: 'text.secondary',
          userSelect: 'none',
          pointerEvents: 'none',
          fontSize: isOutlookAddin ? '12px' : '14px',
          maxWidth: isOutlookAddin ? '160px' : '330px'
        }}
        noWrap
      >
        {(isTimer ? activityDescription : timeRecordingActivityDescription) || '--'} &nbsp;|&nbsp; {description || '--'}
      </MUITypography>
    </MUIStack>
  )
}

TimeRecordingDrawerEntry.propTypes = {
  entry: PropTypes.object,
  onClick: PropTypes.func
}
